import { motion, AnimatePresence } from 'framer-motion'
import {
  Clock,
  RefreshCw,
  CheckCircle,
  XCircle,
  PauseCircle,
  AlertTriangle,
  X,
  ChevronDown,
} from 'lucide-react'
import { useState, useEffect } from 'react'
import { Task, TaskStatus } from '@/store/taskStore'
import { cn } from '@/utils'

interface TaskCardProps {
  task: Task
  onCancel?: () => void
  onClick?: () => void
}

const statusConfig: Record<
  TaskStatus,
  { icon: typeof Clock; color: string; gradient: string; label: string }
> = {
  pending: {
    icon: Clock,
    color: 'warning',
    gradient: 'from-amber-500 to-orange-500',
    label: 'Pending',
  },
  running: {
    icon: RefreshCw,
    color: 'blue',
    gradient: 'from-blue-500 to-cyan-500',
    label: 'Running',
  },
  completed: {
    icon: CheckCircle,
    color: 'success',
    gradient: 'from-emerald-500 to-green-500',
    label: 'Completed',
  },
  failed: {
    icon: XCircle,
    color: 'danger',
    gradient: 'from-red-500 to-rose-500',
    label: 'Failed',
  },
  cancelled: {
    icon: XCircle,
    color: 'gray',
    gradient: 'from-gray-500 to-slate-500',
    label: 'Cancelled',
  },
  paused: {
    icon: PauseCircle,
    color: 'warning',
    gradient: 'from-amber-500 to-yellow-500',
    label: 'Paused',
  },
}

export function TaskCard({ task, onCancel, onClick }: TaskCardProps) {
  const [expanded, setExpanded] = useState(false)
  const config = statusConfig[task.status]
  const StatusIcon = config.icon
  const isRunning = task.status === 'running'
  const canCancel = task.status === 'running' || task.status === 'pending'

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9, y: -10 }}
      transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
      whileHover={{ y: -2 }}
      onClick={onClick}
      className="glass-card p-4 cursor-pointer group relative overflow-hidden"
    >
      {/* Animated border glow for running tasks */}
      {isRunning && (
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute inset-0 opacity-50"
            style={{
              background: `linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.2), transparent)`,
              animation: 'shimmer 2s ease-in-out infinite',
            }}
          />
        </div>
      )}

      {/* Header */}
      <div className="flex items-start gap-3 relative z-10">
        {/* Status Icon with gradient background */}
        <div
          className={cn(
            'p-2.5 rounded-xl shrink-0',
            `bg-gradient-to-br ${config.gradient}`
          )}
          style={{
            boxShadow: isRunning
              ? `0 4px 15px ${config.color === 'blue' ? 'rgba(59, 130, 246, 0.4)' : 'transparent'}`
              : undefined,
          }}
        >
          <motion.div
            animate={isRunning ? { rotate: 360 } : undefined}
            transition={isRunning ? { duration: 2, repeat: Infinity, ease: 'linear' } : undefined}
          >
            <StatusIcon className="w-5 h-5 text-white" />
          </motion.div>
        </div>

        {/* Title & Description */}
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-white truncate group-hover:text-gold transition-colors">
            {task.title}
          </h3>
          {task.description && (
            <p className="text-xs text-white/50 truncate mt-0.5">
              {task.description}
            </p>
          )}
        </div>

        {/* Status Badge */}
        <div
          className={cn(
            'px-2.5 py-1 rounded-full text-xs font-medium shrink-0',
            config.color === 'warning' && 'bg-warning/20 text-warning',
            config.color === 'blue' && 'bg-blue/20 text-blue-light',
            config.color === 'success' && 'bg-success/20 text-success',
            config.color === 'danger' && 'bg-danger/20 text-danger',
            config.color === 'gray' && 'bg-white/10 text-white/50'
          )}
        >
          {isRunning ? `${Math.round(task.progress * 100)}%` : config.label}
        </div>

        {/* Cancel Button */}
        {canCancel && onCancel && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => {
              e.stopPropagation()
              onCancel()
            }}
            className="p-1.5 rounded-lg bg-danger/10 text-danger hover:bg-danger/20 transition-all"
          >
            <X className="w-4 h-4" />
          </motion.button>
        )}
      </div>

      {/* Progress Bar with shimmer */}
      <AnimatePresence>
        {isRunning && (
          <motion.div
            initial={{ opacity: 0, height: 0, marginTop: 0 }}
            animate={{ opacity: 1, height: 'auto', marginTop: 16 }}
            exit={{ opacity: 0, height: 0, marginTop: 0 }}
            className="relative z-10"
          >
            <TaskProgressBar progress={task.progress} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error Message */}
      {task.status === 'failed' && task.errorMessage && (
        <motion.div
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-3 p-3 rounded-lg flex items-start gap-2 relative z-10"
          style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
          }}
        >
          <AlertTriangle className="w-4 h-4 text-danger flex-shrink-0 mt-0.5" />
          <p className="text-xs text-danger line-clamp-2">{task.errorMessage}</p>
        </motion.div>
      )}

      {/* Subtasks Accordion */}
      {task.subTasks.length > 0 && (
        <div className="mt-3 relative z-10">
          <button
            onClick={(e) => {
              e.stopPropagation()
              setExpanded(!expanded)
            }}
            className="flex items-center gap-2 text-xs text-white/50 hover:text-white/70 transition-colors"
          >
            <motion.div animate={{ rotate: expanded ? 180 : 0 }}>
              <ChevronDown className="w-4 h-4" />
            </motion.div>
            {task.subTasks.length} subtasks
          </button>

          <AnimatePresence>
            {expanded && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-2 space-y-2 overflow-hidden"
              >
                {task.subTasks.map((subTask) => (
                  <SubTaskItem key={subTask.id} subTask={subTask} />
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </motion.div>
  )
}

// Fun progress messages based on progress percentage
const progressMessages = [
  { threshold: 0, messages: ['Getting started...', 'Warming up...', 'Let me work on that...'] },
  { threshold: 0.2, messages: ['Making progress...', 'Almost there...', 'Working hard...'] },
  { threshold: 0.4, messages: ['Halfway there! 🚀', 'Looking good...', 'Building something cool...'] },
  { threshold: 0.6, messages: ['Getting closer...', 'Almost done! ⚡', 'Polishing things up...'] },
  { threshold: 0.8, messages: ['Final touches...', 'Nearly ready! ✨', 'Just a bit more...'] },
  { threshold: 0.95, messages: ['Wrapping up! 🎉', 'Done in a sec...', 'Finishing strong!'] },
]

function getProgressMessage(progress: number): string {
  const applicable = progressMessages.filter(p => progress >= p.threshold).pop()
  if (!applicable) return 'Working...'
  return applicable.messages[Math.floor(Math.random() * applicable.messages.length)]
}

function TaskProgressBar({ progress }: { progress: number }) {
  const percentage = Math.min(progress * 100, 100)
  const [message, setMessage] = useState(getProgressMessage(progress))

  // Update message periodically for running tasks
  useEffect(() => {
    const interval = setInterval(() => {
      setMessage(getProgressMessage(progress))
    }, 3000)
    return () => clearInterval(interval)
  }, [progress])

  return (
    <div className="space-y-2">
      {/* Progress message */}
      <div className="flex items-center justify-between text-xs">
        <motion.span
          key={message}
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-white/60"
        >
          {message}
        </motion.span>
        <span className="text-emerald-400 font-medium">{Math.round(percentage)}%</span>
      </div>

      {/* Progress bar */}
      <div className="relative h-2.5 bg-white/10 rounded-full overflow-hidden">
        {/* Animated background pattern */}
        <motion.div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(255,255,255,0.1) 5px, rgba(255,255,255,0.1) 10px)',
            backgroundSize: '20px 20px',
          }}
          animate={{ x: [0, 20] }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        />

        {/* Progress fill with gradient */}
        <motion.div
          className="absolute inset-y-0 left-0 rounded-full"
          style={{
            background: 'linear-gradient(90deg, #4CD137, #72E05B, #4CD137)',
            backgroundSize: '200% 100%',
            boxShadow: '0 0 20px rgba(76, 209, 55, 0.6)',
          }}
          initial={{ width: 0 }}
          animate={{
            width: `${percentage}%`,
            backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
          }}
          transition={{
            width: { duration: 0.5, ease: 'easeOut' },
            backgroundPosition: { duration: 2, repeat: Infinity, ease: 'linear' }
          }}
        />

        {/* Shimmer effect */}
        <motion.div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
            backgroundSize: '200% 100%',
          }}
          animate={{ backgroundPosition: ['-200% 50%', '200% 50%'] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
        />

        {/* Pulsing glow at progress edge */}
        <motion.div
          className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(76, 209, 55, 1) 0%, transparent 70%)',
            left: `calc(${Math.max(percentage - 2, 0)}% - 8px)`,
          }}
          animate={{
            scale: [1, 1.5, 1],
            opacity: [0.8, 1, 0.8]
          }}
          transition={{ duration: 0.8, repeat: Infinity }}
        />
      </div>
    </div>
  )
}

function SubTaskItem({ subTask }: { subTask: { id: string; title: string; status: TaskStatus } }) {
  const config = statusConfig[subTask.status]
  const Icon = config.icon

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      className="flex items-center gap-2 py-1.5 px-2 rounded-lg bg-white/5"
    >
      <div
        className={cn(
          'w-5 h-5 rounded-full flex items-center justify-center',
          `bg-gradient-to-br ${config.gradient}`
        )}
      >
        <Icon className="w-3 h-3 text-white" />
      </div>
      <span className="text-xs text-white/70 truncate flex-1">
        {subTask.title}
      </span>
    </motion.div>
  )
}

// Empty state for task list
export function TaskEmptyState() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-center py-12"
    >
      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-white/5 flex items-center justify-center">
        <Clock className="w-8 h-8 text-white/30" />
      </div>
      <h3 className="text-lg font-medium text-white/70 mb-2">No tasks yet</h3>
      <p className="text-sm text-white/40">
        Ask Niki to help you with something!
      </p>
    </motion.div>
  )
}
